import { Draft04, type Draft, type JsonError } from "json-schema-library";
import {
    getManifestSchema,
    getLatestBlockTypeForFamily,
    getBlockTypeForPolicyCheck,
    getWorkflowRefs
} from "./workflowManifest";
import { modelToBlockMap } from "./EditorComponents/modelToBlockMap";
import { getWorkspacePlan } from "../../../routes/workspace/utils";
import type { PreviewImageInput, ResizedImage } from "./EditorComponents/core/types";
import { WorkflowEvents, type WorkflowEventPayloadMap } from "./analytics/WorkflowEvents";

// Server URL localStorage persistence helpers
export function getStoredServerUrl(workspaceId: string | undefined): string | null {
    if (!workspaceId) return null;
    try {
        const stored = localStorage.getItem("inferenceServerUrl");
        if (stored) {
            const parsed = JSON.parse(stored);
            return parsed[workspaceId] || null;
        }
    } catch {
        // ignore parse errors
    }
    return null;
}

export function saveServerUrl(workspaceId: string | undefined, url: string): void {
    if (!workspaceId) return;
    try {
        const stored = localStorage.getItem("inferenceServerUrl");
        const parsed = stored ? JSON.parse(stored) : {};
        parsed[workspaceId] = url;
        localStorage.setItem("inferenceServerUrl", JSON.stringify(parsed));
    } catch {
        // ignore storage errors
    }
}

// block-name -> model-name
const allowedTextImagePairMap = {
    florence_2: "florence-2",
    qwen25vl: "qwen25",
    qwen3vl: "qwen3vl",
    qwen3_5vl: "qwen3_5",
    smolvlm2: "smolvlm2",
    moondream2: "moondream2"
};

export const validTextImagePairsModels = Object.values(allowedTextImagePairMap);
export const validTextImagePairsBlocks = Object.keys(allowedTextImagePairMap);

export function getWorkflowBlockCounts(blocks) {
    let blockCounts = blocks.reduce((acc, block) => {
        acc[block.type] = (acc[block.type] || 0) + 1;
        return acc;
    }, {});
    return { totalCount: blocks.length, blocksUsed: blockCounts };
}

export function trackEvent<E extends WorkflowEvents>(
    type: E,
    data: WorkflowEventPayloadMap[E]
): void;
export function trackEvent(type: string, data?: any): void;
export function trackEvent(type: string, data?: any) {
    if (window.eventTracker) {
        // Get current user's ID if authenticated
        const user_id = window.user?.uid;

        window.eventTracker.event({
            type,
            data: { ...data, plan: getWorkspacePlan(window.usage), ...(user_id ? { user_id } : {}) }
        });
    } else {
        // eslint-disable-next-line no-console
        console.log({ type, data });
    }
}

export function isImageInputType(input) {
    return ["InferenceImage", "WorkflowImage"].includes(input?.type);
}

export async function isImageUrl(url) {
    let isValidUrl;
    try {
        new URL(url);
        isValidUrl = true;
    } catch (e) {
        return false;
    }

    try {
        const response = await fetch(url, { method: "HEAD" });
        const contentType = response.headers.get("Content-Type");
        return response.ok && contentType && contentType.startsWith("image/");
    } catch (error) {
        return false;
    }
}

/**
 * Normalizes a media input object by determining its type and setting default values
 * for mediaType, videoType, and validUrl properties.
 * This eliminates duplication between useEffect and onUseExample in PreviewWorkflow.
 */
export function normalizeMediaInput(
    input: PreviewImageInput,
    forceImageType: boolean = false
): PreviewImageInput {
    if (!input?.value) return input;

    // Determine the type based on the value format
    let inputType = input.type;
    if (!inputType) {
        if (input.value?.startsWith("data:")) {
            inputType = "base64";
        } else if (input.value?.startsWith("http") || input.value?.startsWith("https")) {
            inputType = "url";
        } else {
            inputType = "url"; // Default to URL type
        }
    }

    return {
        ...input,
        mediaType: forceImageType ? "image" : input.mediaType || "image",
        type: inputType,
        videoType: forceImageType ? "file" : input.videoType || "file",
        validUrl: inputType === "url" && input.value ? true : input.validUrl
    };
}

export function getPreviewImageUrl(previewImage: PreviewImageInput): string | null {
    const mediaType = previewImage?.mediaType || "image"; // default media is image
    if (mediaType === "image") {
        if (previewImage?.type === "base64" || previewImage?.validUrl) {
            return previewImage.prefix
                ? previewImage.prefix + previewImage.value
                : previewImage.value;
        }
    }
    return null;
}

/**
 * Resizes an image to the specified dimensions
 * @param {HTMLImageElement} image - The image element to resize
 * @param {number} width - Original width
 * @param {number} height - Original height
 * @param {number} newWidth - Target width
 * @param {number} newHeight - Target height
 * @param {string} fileType - Original file MIME type (e.g., "image/jpeg")
 * @returns {Object} Resized image data with value, prefix, width, and height
 */
function resizeImage(
    image: HTMLImageElement,
    width: number,
    height: number,
    newWidth: number,
    newHeight: number,
    fileType: string
): ResizedImage {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    canvas.width = newWidth;
    canvas.height = newHeight;
    ctx.drawImage(image, 0, 0, width, height, 0, 0, newWidth, newHeight);
    const resizedDataUrl = canvas.toDataURL(fileType);
    const imagePrefixMatch = resizedDataUrl.match(/^data:image\/([^;]+);base64,/);
    const imagePrefix = imagePrefixMatch ? imagePrefixMatch[0] : "";

    return {
        value: resizedDataUrl.replace(imagePrefix, ""),
        prefix: imagePrefix,
        width: newWidth,
        height: newHeight
    };
}

/**
 * Handles image selection with automatic resizing based on deployment type
 * @param {FileList} files - Selected files
 * @param {Function} onImageSelected - Callback function when image is processed
 * @param {boolean} isHostedV1 - Whether using Hosted V1 API
 * @param {boolean} isServerless - Whether using Serverless API
 */
export function handleImageSelected(
    files: FileList | File[],
    onImageSelected: (data: PreviewImageInput) => void,
    isHostedV1 = false,
    isServerless = true
): PreviewImageInput {
    if (files.length <= 0) {
        return;
    }
    const file = files[0];
    if (file && file.type.substr(0, 5) === "image") {
        const reader = new FileReader();
        reader.onloadend = () => {
            const img = new Image();
            img.onload = () => {
                const { width, height } = img;

                let encodedImage = img.src;
                let newDataUrl = encodedImage;
                const originalDataUrl = encodedImage;
                let imageSize = atob(newDataUrl.split(",")[1]).length;

                let newWidth = width;
                let newHeight = height;
                let resizedHostedValue: ResizedImage | null = null;
                let resizedServerlessValue: ResizedImage | null = null;

                // check if image is higher than hosted limit
                const MAX_SIZE_HOSTED = 3 * 1024 * 1024;
                const MAX_DIMENSION_HOSTED = 2048;

                const sizeExceeded = imageSize > MAX_SIZE_HOSTED;
                const dimensionsExceeded =
                    width > MAX_DIMENSION_HOSTED || height > MAX_DIMENSION_HOSTED;

                if (sizeExceeded || dimensionsExceeded) {
                    const sizeScale = sizeExceeded
                        ? Math.min(1, Math.sqrt(MAX_SIZE_HOSTED / imageSize))
                        : 1;
                    const dimensionScale = Math.min(
                        1,
                        MAX_DIMENSION_HOSTED / Math.max(width, height)
                    );
                    const scale = Math.min(dimensionScale, sizeScale);
                    newWidth = Math.round(width * scale);
                    newHeight = Math.round(height * scale);
                    resizedHostedValue = resizeImage(
                        img,
                        width,
                        height,
                        newWidth,
                        newHeight,
                        file.type
                    );
                }

                // check if image is higher than serverless limit
                // Serverless: resize if > 20MB
                const MAX_SIZE_SERVERLESS = 20 * 1024 * 1024;

                if (imageSize > MAX_SIZE_SERVERLESS) {
                    const scale = Math.min(1, Math.sqrt(MAX_SIZE_SERVERLESS / imageSize));
                    newWidth = Math.round(width * scale);
                    newHeight = Math.round(height * scale);
                    resizedServerlessValue = resizeImage(
                        img,
                        width,
                        height,
                        newWidth,
                        newHeight,
                        file.type
                    );
                }

                const imagePrefixMatch = originalDataUrl.match(/^data:image\/([^;]+);base64,/);
                const imagePrefix = imagePrefixMatch ? imagePrefixMatch[0] : "";

                const originalEncodedImage = {
                    value: originalDataUrl.replace(imagePrefix, ""),
                    prefix: imagePrefix,
                    width: width,
                    height: height
                };
                const resizedType =
                    resizedHostedValue && isHostedV1
                        ? "hosted"
                        : resizedServerlessValue && isServerless
                          ? "serverless"
                          : null;
                const currentImage =
                    resizedType === "hosted"
                        ? resizedHostedValue
                        : resizedType === "serverless"
                          ? resizedServerlessValue
                          : originalEncodedImage;

                onImageSelected({
                    mediaType: "image",
                    videoType: null,
                    type: "base64",
                    value: currentImage.value,
                    originalValue: originalEncodedImage,
                    resizedServerlessValue: resizedServerlessValue,
                    resizedHostedValue: resizedHostedValue,
                    path: file.name,
                    prefix: currentImage.prefix,
                    resized: resizedType !== null,
                    originalDimensions: { width: width, height: height },
                    newDimensions: { width: currentImage.width, height: currentImage.height }
                });
            };
            img.src = reader.result as string;
        };
        reader.readAsDataURL(file);
    } else {
        throw new Error("Invalid file type");
    }
}

/**
 * Prepare the request removing images that are not needed to be sent to the server.
 * @param {*} inputs
 * @param {*} isHostedV1
 * @param {*} isServerless
 * @returns a new object with the inputs sanitized
 */
export function sanitizeInputsForRequest({
    inputs,
    isHostedV1,
    isServerless
}: {
    inputs: Record<string, PreviewImageInput>;
    isHostedV1: boolean;
    isServerless: boolean;
}) {
    return Object.fromEntries(
        Object.entries(inputs).map(([key, input]) => {
            if (input.newDimensions && input.originalDimensions) {
                const { originalValue, resizedHostedValue, resizedServerlessValue, ...rest } =
                    input;
                const targetValue =
                    isHostedV1 && resizedHostedValue
                        ? resizedHostedValue
                        : isServerless && resizedServerlessValue
                          ? resizedServerlessValue
                          : originalValue;

                return [key, { ...rest, value: targetValue.value, prefix: targetValue.prefix }];
            }
            return [key, input];
        })
    );
}

/**
 * Validates a workflow specification against the manifest schema.
 * Follows the same validation mechanism as the workflow JSON editor.
 *
 * @param workflowSpec - The workflow specification to validate.
 * @returns An array of errors if the specification is invalid.
 */
export function validateWorkflowSpecification(workflowSpec: any): JsonError[] {
    const manifestSchema = getManifestSchema();
    const validator: Draft = new Draft04(manifestSchema);
    return validator.validate(workflowSpec);
}

/**
 * Validates that all blocks in a workflow specification are allowed in the workspace.
 * This is a thin wrapper around the shared validation logic in blockPermissionsValidator.js
 *
 * @param specification - The workflow specification containing steps and dynamic_blocks_definitions
 * @param allowedWorkflowBlocks - Array of allowed block types from workspace, or null/undefined if all blocks allowed
 * @returns Object with validation result and list of disabled blocks if any
 */
export function validateWorkflowBlockPermissions(
    specification: any,
    allowedWorkflowBlocks: string[] | null | undefined
): { isValid: boolean; disabledBlocks: string[] } {
    // Use shared validation logic from blockPermissionsValidator
    // Note: This file is in src/scripts/workflows and gets synced to functions/scripts/workflows
    const {
        validateWorkflowBlockPermissions: sharedValidator
    } = require("../../../workflows/blockPermissionsValidator");

    const result = sharedValidator(
        specification,
        allowedWorkflowBlocks,
        getBlockTypeForPolicyCheck
    );

    // Return result in the format expected by frontend callers
    // Note: hasDisabledCustomPythonBlocks is factored into isValid, so we don't need to expose it separately
    return { isValid: result.isValid, disabledBlocks: result.disabledBlocks };
}

/**
 * Maps model types to their corresponding workflow block types.
 * Handles both single-label and multi-label classification.
 * Returns the manifest_type_identifier with the latest version.
 *
 * @param modelType - The model type (e.g., "object-detection", "classification")
 * @param modelId - The specific model ID (e.g. "qwen25-vl-7b-peft")
 * @returns The corresponding workflow block type with version
 */
export function getBlockTypeFromModelType(modelType: string, modelId?: string): string {
    // First, try to find a specific mapping for the model ID
    if (modelId && modelToBlockMap[modelId]) {
        const blockFamily = modelToBlockMap[modelId];
        return getLatestBlockTypeForFamily(blockFamily);
    }

    // For text-image-pairs, try to find a matching block
    if (modelType === "text-image-pairs") {
        if (modelId && modelToBlockMap[modelId]) {
            return getLatestBlockTypeForFamily(modelToBlockMap[modelId]);
        }
        return null;
    }

    // Try to find mapping for the model type
    const blockFamily = modelToBlockMap[modelType];
    if (!blockFamily) return null;

    // Get the latest version using the manifest helper
    return getLatestBlockTypeForFamily(blockFamily);
}

/**
 * Normalizes a model type to handle multilabel classification special cases.
 * Converts "classification" + multilabel flag to "classification-multi" or "classification-single".
 *
 * @param modelType - The original model type
 * @param isMultilabel - Whether the model is multilabel
 * @returns The normalized model type
 */
export function normalizeModelType(modelType: string, isMultilabel?: boolean): string {
    if (modelType === "classification") {
        return isMultilabel ? "classification-multi" : "classification-single";
    }
    return modelType;
}

/**
 * Coerce/expand legacy allow tokens into canonical allowed model type tokens.
 *
 * Canonical:
 * - classification-single | classification-multi
 * - object-detection | instance-segmentation | keypoint-detection
 * - text-image-pairs (allow any text-image model)
 * - text-image-pairs-<blockKey> (allow only models compatible with that block)
 *
 * Legacy supported at boundaries:
 * - classification -> classification-single + classification-multi
 * - multi-label-classification -> classification-multi
 * - <blockKey> where blockKey is a text-image-pairs block (e.g. florence_2) -> text-image-pairs-<blockKey>
 */
export function coerceAllowedModelTypes(
    allowedModelTypes: string[] | null | undefined
): string[] | null {
    if (!allowedModelTypes) {
        return null;
    }

    const expanded = new Set<string>();
    for (const raw of allowedModelTypes) {
        const t = (raw || "").trim();
        if (!t) continue;

        if (t === "classification") {
            expanded.add("classification-single");
            expanded.add("classification-multi");
            continue;
        }
        if (t === "multi-label-classification") {
            expanded.add("classification-multi");
            continue;
        }
        if (t === "text-image-pairs") {
            expanded.add("text-image-pairs");
            continue;
        }
        if (t.startsWith("text-image-pairs-")) {
            expanded.add(t);
            continue;
        }
        if (validTextImagePairsBlocks.includes(t)) {
            expanded.add(`text-image-pairs-${t}`);
            continue;
        }

        expanded.add(t);
    }

    return Array.from(expanded);
}

/**
 * Checks if a project/model is compatible with the allowed model types.
 * Handles multilabel classification special cases.
 *
 * @param project - Project/model object with type and multilabel properties
 * @param allowedModelTypes - Array of allowed model types, or null if no restriction
 * @returns Whether the project is compatible
 */
export function isModelTypeCompatible(
    model: { type: string; multilabel?: boolean; modelType?: string },
    allowedModelTypes: string[] | null
): boolean {
    const allowed = coerceAllowedModelTypes(allowedModelTypes);

    if (model.type === "text-image-pairs") {
        if (!model.modelType || typeof model.modelType !== "string") {
            return false;
        }
        const modelType = model.modelType;

        const isValidTextImagePairsModel = validTextImagePairsModels.some((validModel) =>
            modelType.includes(validModel)
        );

        if (!isValidTextImagePairsModel) {
            return false;
        }

        if (!allowed) {
            return true;
        }

        // Allow any text-image-pairs model when explicitly allowed.
        if (allowed.includes("text-image-pairs")) {
            return true;
        }

        // Block-scoped allow tokens: text-image-pairs-<blockKey>
        const allowedBlockKeys = allowed
            .filter((t) => t.startsWith("text-image-pairs-"))
            .map((t) => t.replace("text-image-pairs-", ""))
            .filter(Boolean);

        if (allowedBlockKeys.length === 0) {
            return false;
        }

        return allowedBlockKeys.some((blockKey) => {
            const mapped = allowedTextImagePairMap[blockKey];
            if (!mapped) return false;
            return modelType.includes(mapped);
        });
    }

    if (!allowed) {
        return true;
    }

    if (!model.type) {
        return false;
    }

    const normalized = normalizeModelType(model.type, model.multilabel);
    return allowed.includes(normalized);
}

/**
 * Single source of truth for model-type allowlists given a workflow model block type.
 * Used by both V1 and V2 model selection UIs to prevent incompatible model selection.
 */
export function getAllowedModelTypesForModelBlock(blockType: string): string[] | null {
    if (!blockType || typeof blockType !== "string") {
        return null;
    }

    const canonical = blockType.split("@")[0];

    switch (canonical) {
        case "roboflow_core/roboflow_object_detection_model":
            return ["object-detection"];
        case "roboflow_core/roboflow_instance_segmentation_model":
            return ["instance-segmentation"];
        case "roboflow_core/roboflow_semantic_segmentation_model":
            return ["semantic-segmentation"];
        case "roboflow_core/roboflow_keypoint_detection_model":
            return ["keypoint-detection"];
        case "roboflow_core/roboflow_classification_model":
            return ["classification-single"];
        case "roboflow_core/roboflow_multi_label_classification_model":
            return ["classification-multi"];
        default:
            break;
    }

    // Text-image-pairs blocks are block-scoped (e.g. florence_2, qwen25vl, ...)
    if (canonical.startsWith("roboflow_core/")) {
        const blockKey = canonical.replace("roboflow_core/", "");
        if (validTextImagePairsBlocks.includes(blockKey)) {
            return [`text-image-pairs-${blockKey}`];
        }
    }

    return null;
}

/**
 * Maps a workflow block type to its corresponding model types.
 * Handles versioned block types by removing the version suffix.
 *
 * @param blockType - The workflow block type (e.g., "roboflow_core/roboflow_object_detection_model@v1")
 * @returns Array of corresponding model types
 */
export function getModelTypesFromBlockType(blockType: string): string[] {
    // Remove version from block type
    const blockFamily = blockType.split("@")[0];

    const blockToModelMapping = {
        "roboflow_core/roboflow_object_detection_model": ["object-detection"],
        "roboflow_core/roboflow_instance_segmentation_model": ["instance-segmentation"],
        "roboflow_core/semantic_segmentation_model": ["semantic-segmentation"],
        "roboflow_core/roboflow_keypoint_detection_model": ["keypoint-detection"],
        "roboflow_core/roboflow_classification_model": ["classification-single"],
        "roboflow_core/roboflow_multi_label_classification_model": ["classification-multi"],
        // Text-image-pairs blocks - each should only show models compatible with that specific block
        "roboflow_core/florence_2": ["text-image-pairs-florence_2"],
        "roboflow_core/qwen25vl": ["text-image-pairs-qwen25vl"],
        "roboflow_core/qwen3vl": ["text-image-pairs-qwen3vl"],
        "roboflow_core/qwen3_5vl": ["text-image-pairs-qwen3_5vl"],
        "roboflow_core/smolvlm2": ["text-image-pairs-smolvlm2"],
        "roboflow_core/moondream2": ["text-image-pairs-moondream2"]
    };

    return blockToModelMapping[blockFamily] || [];
}

export function isStatelessWorkflow(workflowConfigJSON?: string): boolean {
    if (!workflowConfigJSON) {
        // console.log("isStatelessWorkflow: No config provided");
        return false;
    }

    try {
        const config = JSON.parse(workflowConfigJSON);
        const spec = config?.specification || config;
        const steps = spec?.steps || [];

        // console.log("isStatelessWorkflow: Checking workflow", {
        //     hasSteps: !!steps.length,
        //     stepCount: steps.length,
        //     steps: steps.map((s: any) => s.type)
        // });

        const allowedBlocks = [
            "roboflow_core/sam3",
            "roboflow_core/seg-preview",
            "roboflow_core/mask_visualization",
            "roboflow_core/bounding_box_visualization",
            "roboflow_core/label_visualization",
            "roboflow_core/polygon_visualization",
            "roboflow_core/color_visualization",
            "roboflow_core/circle_visualization",
            "roboflow_core/dot_visualization",
            "roboflow_core/ellipse_visualization",
            "roboflow_core/triangle_visualization",
            "roboflow_core/halo_visualization",
            "roboflow_core/blur_visualization",
            "roboflow_core/pixelate_visualization",
            "roboflow_core/crop_visualization",
            "roboflow_core/background_color_visualization"
        ];

        const isStateless = steps.every((step: any) => {
            const type = step.type;
            const isAllowed = allowedBlocks.some((allowed) => type.startsWith(allowed));
            return isAllowed;
        });

        // console.log("isStatelessWorkflow: Result", { isStateless });
        return isStateless;
    } catch (e) {
        // eslint-disable-next-line no-console
        console.error("isStatelessWorkflow: Error parsing workflow config", e);
        return false;
    }
}

/**
 * Infers the JavaScript type from a workflow parameter's default value.
 * Used for code generation to produce type-appropriate code snippets.
 */
export function inferWorkflowParameterType(value: any): string {
    if (value === "" || value === undefined || value === null) return "string";
    if (value === "true" || value === "false" || typeof value === "boolean") return "boolean";
    if (typeof value === "number" || !isNaN(value)) return "number";
    try {
        const parsed = typeof value === "string" ? JSON.parse(value) : value;
        if (Array.isArray(parsed) || typeof parsed === "object") return "object";
    } catch {
        return "string";
    }
    return "string";
}

/**
 * Parses a workflow parameter value into its proper typed representation.
 * Returns a placeholder string for missing values to indicate user input needed.
 */
export function parseWorkflowParameterValue(value: any, type: string): any {
    if (value === undefined || value === null || value === "") return "PARAMETER_VALUE";
    if (type === "object") {
        return typeof value === "string" ? JSON.parse(value) : value;
    }
    if (type === "number") return Number(value);
    if (type === "boolean") return value === "true" || value === true;
    return value;
}

/**
 * Transforms a workflow specification to include all block outputs.
 * Used by debug mode to return intermediate step results.
 */
export function addAllRefsToOutputs(workflowSpec: any) {
    const refOptions = getWorkflowRefs(workflowSpec);

    const outputs = refOptions
        .filter((option: any) => !option.ref.endsWith(".*"))
        .map((option: any) => ({
            type: "JsonField",
            name: option.ref.replace(/\./g, "/"),
            coordinates_system: "own",
            selector: option.ref
        }));

    return { ...workflowSpec, outputs };
}
